import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    FileText,
    Download,
    TrendingUp,
    ArrowUpRight,
    ArrowDownRight,
    BarChart3
} from 'lucide-react';
import { cn } from '../lib/utils';

const PLStatements: React.FC = () => {
    const [statements, setStatements] = useState<any[]>([]);

    useEffect(() => {
        api.get('/pl-statements').then(res => setStatements(res.data));
    }, []);

    return (
        <div className="space-y-8 max-w-4xl mx-auto pb-10">
            <div>
                <h1 className="text-3xl font-bold text-slate-900">Financial Statements</h1>
                <p className="text-slate-500 mt-1">Monthly P&L and audit reports for transparency</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {statements.map((s) => (
                    <div key={s.id} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:border-violet-300 transition-all flex flex-col">
                        <div className="flex justify-between items-start mb-6">
                            <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center text-violet-600">
                                <BarChart3 size={20} />
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                                {s.period}
                            </span>
                        </div>

                        <h4 className="font-bold text-slate-900 leading-tight mb-2">{s.title}</h4>
                        <p className="text-[10px] text-slate-400 font-bold uppercase mb-6">
                            {new Date(s.period_start).toLocaleDateString()} - {new Date(s.period_end).toLocaleDateString()}
                        </p>

                        <div className="space-y-3 mb-6">
                            <div className="flex justify-between text-xs">
                                <span className="text-slate-500">Income</span>
                                <span className="font-bold text-emerald-600">₹{s.total_income.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-xs">
                                <span className="text-slate-500">Expense</span>
                                <span className="font-bold text-red-500">₹{s.total_expense.toLocaleString()}</span>
                            </div>
                            <div className="pt-3 border-t border-slate-50 flex justify-between">
                                <span className="text-xs font-bold text-slate-900">Net Balance</span>
                                <div className={cn(
                                    "text-xs font-bold flex items-center gap-1",
                                    s.net_balance >= 0 ? "text-emerald-600" : "text-red-700"
                                )}>
                                    {s.net_balance >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                                    ₹{Math.abs(s.net_balance).toLocaleString()}
                                </div>
                            </div>
                        </div>

                        {s.file_url && (
                            <a
                                href={s.file_url}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-auto w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-2.5 rounded-xl font-bold text-sm hover:bg-slate-800 transition-all"
                            >
                                <Download size={16} />
                                Download PDF
                            </a>
                        )}
                    </div>
                ))}
                {statements.length === 0 && (
                    <div className="col-span-full py-20 text-center bg-white rounded-2xl border border-dashed border-slate-200">
                        <TrendingUp size={40} className="text-slate-200 mx-auto mb-3" />
                        <p className="text-slate-500 font-medium">No financial reports published yet</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PLStatements;
